import {DestroyRef, inject, Injectable} from '@angular/core';
import {
  FormGroup,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  AvailableOperators,
  isAllProductsFilter,
  isBestPriceFilter,
  isCategoryFilter,
  isDaysValueFilter,
  isFetchStatusFilter,
  isLogicalConditionFilter,
  isMostUsedPriceFilter,
  isOutliersFilter,
  isPriceChangesFilter,
  isProductPerformanceFilter,
  isWithCompetitorsFilter,
  ProductGroupFilterAllProducts,
  ProductGroupFilterBestPrice,
  ProductGroupFilterCategory,
  ProductGroupFilterDaysValue,
  ProductGroupFilterEntity,
  ProductGroupFilterFetchStatus,
  ProductGroupFilterKey,
  ProductGroupFilterLogicalCondition,
  ProductGroupFilterMostUsedPrice,
  ProductGroupFilterOutliers,
  ProductGroupFilterPriceChanges,
  ProductGroupFilterProductPerformance,
  ProductGroupFilterWithCompetitors,
} from '@em/data-feed/util-types';
import {
  ConditionFormBuilder,
  OperatorGroups,
} from '@em/shared/mapping-engine/domain-models';
import {startWith} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Injectable()
export class ProductGroupFilterFormBuilderService extends ConditionFormBuilder<ProductGroupFilterEntity> {
  private readonly _fb = inject(UntypedFormBuilder);
  private readonly _destroyRef = inject(DestroyRef);

  override buildCondition(
    condition: ProductGroupFilterEntity,
  ): UntypedFormGroup {
    const form: FormGroup = this._fb.group({
      key: [condition.key || '', Validators.required],
    });

    if (condition?.key) {
      this._keyChanged(condition, form);
    }

    form
      .get('key')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((newKey: ProductGroupFilterKey) => {
        this._keyChanged({key: newKey}, form);
      });

    return form;
  }

  isRequiredRightOperand(
    operatorGroups: OperatorGroups | undefined,
    operatorName: string | undefined,
  ): boolean {
    return !!operatorGroups?.some((group) => {
      const operator = group.operators.find((op) => op.name === operatorName);
      return operator && operator.requireRightOperand;
    });
  }

  private _keyChanged(condition: ProductGroupFilterEntity, form: FormGroup) {
    if (isCategoryFilter(condition)) {
      this._buildCategoriesForm(form, condition);
    } else if (isBestPriceFilter(condition)) {
      this._buildBestPriceForm(form, condition);
    } else if (isMostUsedPriceFilter(condition)) {
      this._buildMostUsedPriceForm(form, condition);
    } else if (isPriceChangesFilter(condition)) {
      this._buildPriceChangesForm(form, condition);
    } else if (isFetchStatusFilter(condition)) {
      this._buildFetchStatusForm(form, condition);
    } else if (isOutliersFilter(condition)) {
      this._buildOutliersForm(form, condition);
    } else if (isDaysValueFilter(condition)) {
      this._buildDaysValueForm(form, condition);
    } else if (isWithCompetitorsFilter(condition)) {
      this._buildWithCompetitorsForm(form, condition);
    } else if (isProductPerformanceFilter(condition)) {
      this._buildProductPerformanceForm(form, condition);
    } else if (isAllProductsFilter(condition)) {
      this._buildAllProductsForm(form, condition);
    } else if (isLogicalConditionFilter(condition)) {
      this._buildLogicalConditionForm(form, condition);
    }
  }

  private _buildLogicalConditionForm(
    form: FormGroup,
    condition: ProductGroupFilterLogicalCondition,
  ) {
    // if previous is already a conditional, don't remove its controls
    if (!form.get('operator') || !form.get('rightOperand')) {
      this._removeControlsOtherThan(['key'], form);
    }

    // check if already a logical condition form to avoid duplicating controls
    if (!form.get('operator') && !form.get('rightOperand')) {
      form.addControl(
        'operator',
        this._fb.control(condition.operator || '', Validators.required),
      );

      form.addControl(
        'rightOperand',
        this._fb.control(condition.rightOperand ?? null),
      );

      form
        .get('operator')
        ?.valueChanges.pipe(
          startWith(form.get('operator')?.value),
          takeUntilDestroyed(this._destroyRef),
        )
        .subscribe((newOperator: string | null | undefined) => {
          //To validate the Operator, required to display error message in that specific field
          // while filterConditionValidator will validate the entire form group
          this._checkRequiredRightOperand(form, newOperator);
        });
    }
  }

  private _buildCategoriesForm(
    form: FormGroup,
    condition: ProductGroupFilterCategory,
  ) {
    this._removeControlsOtherThan(['key'], form);

    // check if already a logical condition form to avoid duplicating controls
    if (!form.get('categories') && !form.get('excludeProducts')) {
      form.addControl(
        'categories',
        this._fb.control(condition.categories || [], [
          Validators.required,
          Validators.minLength(1),
        ]),
      );
      form.addControl(
        'excludeProducts',
        this._fb.control(condition.excludeProducts || false),
      );
    }
  }

  private _buildBestPriceForm(
    form: FormGroup,
    condition: ProductGroupFilterBestPrice,
  ) {
    this._removeControlsOtherThan(['key'], form);

    form.addControl(
      'toleranceEnabled',
      this._fb.control(condition.toleranceEnabled || false, [
        Validators.required,
      ]),
    );
    form.addControl(
      'tolerancePercent',
      this._fb.control(condition.tolerancePercent || 0),
    );
    form.addControl(
      'shippingCostsExcluded',
      this._fb.control(condition.shippingCostsExcluded || false),
    );
    form.addControl(
      'excludeProducts',
      this._fb.control(condition.excludeProducts || false),
    );

    form
      .get('toleranceEnabled')
      ?.valueChanges.pipe(
        startWith(form.get('toleranceEnabled')?.value),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((toleranceEnabled: boolean | null | undefined) => {
        if (toleranceEnabled) {
          form
            .get('tolerancePercent')
            ?.setValidators([
              Validators.required,
              Validators.min(1),
              Validators.max(100),
            ]);
          form.get('tolerancePercent')?.updateValueAndValidity();
        } else {
          form.get('tolerancePercent')?.setValidators(null);
          form.get('tolerancePercent')?.setValue(null);
          form.get('tolerancePercent')?.updateValueAndValidity();
        }
      });
  }
  private _buildMostUsedPriceForm(
    form: FormGroup,
    condition: ProductGroupFilterMostUsedPrice,
  ) {
    this._removeControlsOtherThan(['key'], form);

    form.addControl(
      'rangeMode',
      this._fb.control(condition.rangeMode || null, [Validators.required]),
    );
    form.addControl(
      'shippingCostsExcluded',
      this._fb.control(condition.shippingCostsExcluded || false),
    );

    form.addControl(
      'excludeProducts',
      this._fb.control(condition.excludeProducts || false),
    );
  }

  private _buildPriceChangesForm(
    form: FormGroup,
    condition: ProductGroupFilterPriceChanges,
  ) {
    this._removeControlsOtherThan(['key'], form);

    form.addControl(
      'value',
      this._fb.control(condition.value ?? 0, [Validators.required]),
    );
    form.addControl(
      'operator',
      this._fb.control(condition.operator || null, [Validators.required]),
    );
    // Use 7 Days for now
    form.addControl(
      'lastDataPoints',
      this._fb.control(condition.lastDataPoints || 7, [Validators.required]),
    );
    form.addControl(
      'includeWithInsufficientData',
      this._fb.control(condition.includeWithInsufficientData || false),
    );
    form.addControl(
      'column',
      this._fb.control(condition.column || null, [Validators.required]),
    );
    form.addControl(
      'toleranceEnabled',
      this._fb.control(condition.toleranceEnabled || false, [
        Validators.required,
      ]),
    );
    form.addControl(
      'tolerancePercent',
      this._fb.control(condition.tolerancePercent ?? null),
    );
    form.addControl('source', this._fb.control(condition.source || null));

    form
      .get('toleranceEnabled')
      ?.valueChanges.pipe(
        startWith(form.get('toleranceEnabled')?.value),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((toleranceEnabled: boolean | null | undefined) => {
        if (toleranceEnabled) {
          form
            .get('tolerancePercent')
            ?.setValidators([Validators.required, Validators.min(1)]);
          form.get('tolerancePercent')?.updateValueAndValidity();
        } else {
          form.get('tolerancePercent')?.setValidators(null);
          form.get('tolerancePercent')?.setValue(null);
          form.get('tolerancePercent')?.updateValueAndValidity();
        }
      });
  }
  private _buildFetchStatusForm(
    form: FormGroup,
    condition: ProductGroupFilterFetchStatus,
  ) {
    this._removeControlsOtherThan(['key'], form);

    form.addControl('value', this._fb.control(condition.value || null));
    form.addControl(
      'operator',
      this._fb.control(condition.operator || null, [Validators.required]),
    );

    form
      .get('operator')
      ?.valueChanges.pipe(
        startWith(form.get('operator')?.value),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((newOperator: string | null | undefined) => {
        if (newOperator === 'is_empty' || newOperator === 'not_empty') {
          form.get('value')?.setValidators([]);
          form.get('value')?.setValue(null);
          form.get('value')?.disable();
        } else {
          form.get('value')?.setValidators([Validators.required]);
          form.get('value')?.enable();
        }

        form.get('value')?.updateValueAndValidity();
      });
  }

  private _buildOutliersForm(
    form: FormGroup,
    condition: ProductGroupFilterOutliers,
  ) {
    this._removeControlsOtherThan(['key'], form);
    form.addControl(
      'shippingCostsExcluded',
      this._fb.control(condition.shippingCostsExcluded || false),
    );
    form.addControl(
      'excludeProducts',
      this._fb.control(condition.excludeProducts || false),
    );
  }
  private _buildDaysValueForm(
    form: FormGroup,
    condition: ProductGroupFilterDaysValue,
  ) {
    this._removeControlsOtherThan(['key'], form);
    form.addControl(
      'days',
      this._fb.control(condition.days ?? null, [Validators.required]),
    );
    form.addControl(
      'operator',
      this._fb.control(condition.operator || null, [Validators.required]),
    );
  }

  private _buildWithCompetitorsForm(
    form: FormGroup,
    condition: ProductGroupFilterWithCompetitors,
  ) {
    this._removeControlsOtherThan(['key'], form);
    form.addControl('operator', this._fb.control(condition.operator || null));
    form.addControl(
      'value',
      this._fb.control(condition.value ?? null, [Validators.required]),
    );

    form.addControl('source', this._fb.control(condition.source || null));

    form
      .get('operator')
      ?.valueChanges.pipe(
        startWith(form.get('operator')?.value),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((newOperator: string | null | undefined) => {
        if (!newOperator) {
          form.get('value')?.setValidators([]);
          form.get('value')?.setValue(null);
          form.get('value')?.disable();
        } else {
          form.get('value')?.setValidators([Validators.required]);
          form.get('value')?.enable();
        }

        form.get('value')?.updateValueAndValidity();
      });
  }

  private _buildProductPerformanceForm(
    form: FormGroup,
    condition: ProductGroupFilterProductPerformance,
  ) {
    this._removeControlsOtherThan(['key'], form);
    form.addControl(
      'roas',
      this._fb.control(condition.roas ?? null, [Validators.required]),
    );
    form.addControl(
      'costs',
      this._fb.control(condition.costs ?? null, [Validators.required]),
    );
  }

  private _buildAllProductsForm(
    form: FormGroup,
    condition: ProductGroupFilterAllProducts,
  ) {
    this._removeControlsOtherThan(['key'], form);
    // Only key is needed for All Products filter
  }

  private _removeControlsOtherThan(controlNames: string[], form: FormGroup) {
    Object.keys(form.controls).forEach((controlName) => {
      if (!controlNames.includes(controlName)) {
        form.removeControl(controlName);
      }
    });
  }

  private _checkRequiredRightOperand(
    form: FormGroup,
    newOperator: string | null | undefined,
  ) {
    const rightOperandControl = form.get('rightOperand');
    const operator = AvailableOperators.find((op) => op.name === newOperator);
    if (operator?.requireRightOperand) {
      rightOperandControl?.setValidators([Validators.required]);
      rightOperandControl?.updateValueAndValidity();
    } else {
      if (rightOperandControl?.value !== null) {
        rightOperandControl?.setValue(null);
      }

      rightOperandControl?.setValidators(null);
      rightOperandControl?.updateValueAndValidity();
    }
  }
}
